import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Upload.module.scss";
import { Avatar, Dialog, DialogContent } from "@mui/material";
import { useDispatch } from "react-redux";
import { setIsLoginDialog } from "../../redux/reducers/loginSlice";
import { setAlertMessage, setAlertType } from "../../redux/reducers/alertSlice";
import { fetchMySubscriptions, fetchSubscriptionUsage, getSubscriptionUsage } from "../../redux/network/userApi";
import { useNavigate } from "react-router-dom";
import VideoTagsSelectContainer from "../Tags/VideoTagsSelectContainer/VideoTagsSelectContainer";
import ReactGA from "react-ga4";
import S3UploadService from "../../services/s3Upload.service";
import TagHelper from "../../helpers/PostHelpers/TagHelper";

import { Camera, Sun, FileVideo, Globe } from "lucide-react";

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;

const guidelines = [
  {
    id: "1",
    guideline:
      "Position camera 2.5m behind baseline (or as far back as possible). \nUse tripod at 1.5-2m height for stability. Select phone with wide angle zoom of 0.6x or less. Record in landscape mode",
  },
  {
    id: "2",
    guideline: "Use bright, even lighting. Keep court clear of obstructions.",
  },
  {
    id: "3",
    guideline:
      "Format: MP4 only. Resolution: 1080p at 30fps. Maximum size: 2GB.\n Ensure auto-rotate is set to ON while recording",
  },
  {
    id: "4",
    guideline:
      "For the best results use chrome browser for Android and safari browser for iphone. Submit directly through Visist.ai app",
  },
];

const WAIT_MESSAGES = [
  "Please wait… calibrating your upload magic",
  "Hold tight — your video is being fine-tuned for brilliance",
  "Almost there — preparing your file for the studio",
  "Optimizing frames and audio — hang on a sec",
];

const initialUploadState = {
  status: "idle",
  progress: 0,
  loaded: 0,
  total: 0,
  speedMBps: 0,
  etaSeconds: null,
  error: null,
};

export default function Upload({
  folderPath = "test-uploads",
  apiBaseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BORQS_URL,
  onUploadComplete = null,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwtToken = typeof window !== "undefined" ? localStorage.getItem("BQS_TOKEN") : null;
  const uploadServiceRef = useRef(null);
  const currentUploadRef = useRef(null);
  const speedSamplesRef = useRef([]);
  const uploadStartTimeRef = useRef(null);
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [duplicateFileError, setDuplicateFileError] = useState(null);
  const [waitMessage, setWaitMessage] = useState("Please wait… calibrating your upload magic");
  const [selectedTagListInParent, setSelectedTagListInParent] = useState([]);
  const [uploadState, setUploadState] = useState(initialUploadState);
  const [isUploading, setIsUploading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [uploadedVideoData, setUploadedVideoData] = useState(null);
  const [showMetaForm, setShowMetaForm] = useState(false);
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [metaData, setMetaData] = useState({ playerName: "", opponentName: "", playerSide: "near", playerDesignation: "B", matchDate: todayStr, location: "", event: "", matchType: "singles" });
  const [metaError, setMetaError] = useState("");
  const isMatchVideoSelected = useMemo(() => selectedTagListInParent.some((t) => /match.*video|video.*match/i.test((t.tagName || "").toLowerCase())), [selectedTagListInParent]);

  useEffect(() => {
    if (!uploadServiceRef.current) {
      uploadServiceRef.current = new S3UploadService(apiBaseUrl);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    try {
      ReactGA.event({ category: "Upload", action: "view" });
    } catch { }
  }, []);

  useEffect(() => {
    if (isUploading) {
      let idx = 0;
      const interval = setInterval(() => {
        setWaitMessage(WAIT_MESSAGES[idx % WAIT_MESSAGES.length]);
        idx += 1;
      }, 3500);
      return () => clearInterval(interval);
    }
  }, [isUploading]);

  useEffect(() => {
    if (!jwtToken) {
      dispatch(setIsLoginDialog(true));
    }
  }, [jwtToken, dispatch]);

  const createQueryParamsForTags = useCallback(() => {
    if (!selectedTagListInParent.length) {
      return "";
    }
    const TAGS_QUERY_KEY = "&tags=";
    return `${TAGS_QUERY_KEY}${selectedTagListInParent.map((tag) => tag._id).join(",")}`;
  }, [selectedTagListInParent]);

  const handleFileInput = useCallback(
    (event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;

      const isMp4 = file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
      if (!isMp4) {
        dispatch(setAlertType("error"));
        dispatch(setAlertMessage("Only MP4 video format is supported. Please select an MP4 file."));
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        dispatch(setAlertType("error"));
        dispatch(setAlertMessage("File size exceeds 2GB limit. Please select a smaller file."));
        return;
      }

      setSelectedFile(file);
      setDuplicateFileError(null);
      setUploadState(initialUploadState);
      try {
        ReactGA.event({
          category: "Upload",
          action: "select_file",
          label: file?.name,
          value: Math.round((file?.size || 0) / 1024),
        });
      } catch { }
    },
    [dispatch]
  );

  const handleBrowseContainerClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  const handleBrowseContainerKeyDown = useCallback((event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  }, [fileInputRef]);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (file) {
        const isMp4 = file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
        if (!isMp4) {
          dispatch(setAlertType("error"));
          dispatch(setAlertMessage("Only MP4 video format is supported. Please select an MP4 file."));
          event.currentTarget.classList.remove(styles.dragOver);
          return;
        }

        if (file.size > MAX_FILE_SIZE) {
          dispatch(setAlertType("error"));
          dispatch(setAlertMessage("File size exceeds 2GB limit. Please select a smaller file."));
          event.currentTarget.classList.remove(styles.dragOver);
          return;
        }

        setSelectedFile(file);
        setDuplicateFileError(null);
        setUploadState(initialUploadState);
        try {
          ReactGA.event({
            category: "Upload",
            action: "drop_file",
            label: file?.name,
            value: Math.round((file.size || 0) / 1024),
          });
        } catch { }
      }
      event.currentTarget.classList.remove(styles.dragOver);
    },
    [dispatch]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.classList.add(styles.dragOver);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.classList.remove(styles.dragOver);
  }, []);

  const formattedSize = useMemo(() => {
    if (!selectedFile) return null;
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = selectedFile.size;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }, [selectedFile]);

  const estimatedTime = useMemo(() => {
    if (!uploadState.etaSeconds) return "--";
    const minutes = Math.floor(uploadState.etaSeconds / 60);
    const seconds = uploadState.etaSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, [uploadState.etaSeconds]);

  const speedLabel = useMemo(() => {
    if (!uploadState.speedMBps || uploadState.speedMBps <= 0) return "--";
    return `${uploadState.speedMBps.toFixed(2)} MB/s`;
  }, [uploadState.speedMBps]);

  const statusLabel = useMemo(() => {
    switch (uploadState.status) {
      case "uploading":
        return `Uploading (${uploadState.progress}%)`;
      case "paused":
        return "Paused";
      case "completed":
        return "Completed";
      case "failed":
        return "Failed";
      default:
        return "Idle";
    }
  }, [uploadState.status, uploadState.progress]);

  const handleStartUpload = useCallback(async () => {
    console.log("🔷 Upload button clicked!");
    console.log("JWT Token:", !!jwtToken);
    console.log("Selected File:", selectedFile?.name);

    if (!jwtToken) {
      console.log("❌ No JWT token found");
      dispatch(setIsLoginDialog(true));
      dispatch(setAlertType("error"));
      dispatch(setAlertMessage("Please Login to upload video"));
      return;
    }
    if (!selectedFile) {
      console.log("❌ No file selected");
      dispatch(setAlertType("error"));
      dispatch(setAlertMessage("Please select a video to upload"));
      return;
    }

    if (!selectedTagListInParent || selectedTagListInParent.length === 0) {
      console.log("❌ No tag selected");
      dispatch(setAlertType("error"));
      dispatch(setAlertMessage("Please select at least one tag for the video"));
      return;
    }

    try {
      ReactGA.event({
        category: "Upload",
        action: "upload_start",
        label: selectedFile?.name,
        value: Math.round((selectedFile?.size || 0) / 1024),
      });
    } catch { }

    setIsUploading(true);
    setWaitMessage("Verifying subscription quota...");

    try {
      const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
      if (enforcement !== 'off') {
        // Validate subscription quota before allowing upload
        const usageRes = await getSubscriptionUsage(jwtToken, "smartInsights");
        if (usageRes?.code === 200 && usageRes.data) {
          const insightsUsage = usageRes.data.currentPeriod?.insightsUsage || {};
          const planFeatures = usageRes.data.subscription?.plan?.insightsFeatures || {};
          const hasSubscription = !!usageRes.data.subscription;
          const maxUploads = hasSubscription ? (planFeatures.maxVideoUploads ?? Infinity) : 0;
          const used = insightsUsage.videoUploads ??
            insightsUsage.usedVideoUploads ??
            insightsUsage.uploadsUsed ??
            insightsUsage.videoUploadCount ?? 0;

          if (!hasSubscription || used >= maxUploads) {
            setIsUploading(false);
            dispatch(setAlertType("error"));
            dispatch(setAlertMessage(hasSubscription ? "Your Upload Quota has been exhausted. Please Upgrade the plan." : "No active subscription found. Please subscribe to a plan to upload videos."));
            navigate("/game-posts/subscription-pricing");
            return;
          }
        }
      }
    } catch (err) {
      console.error("Failed to check subscription usage:", err);
      // Allow upload to proceed if validation fails rather than locking the user out
    }

    setIsUploading(true);
    setIsPaused(false);
    uploadStartTimeRef.current = Date.now();
    speedSamplesRef.current = [];

    setUploadState({
      status: "uploading",
      progress: 0,
      loaded: 0,
      total: selectedFile.size,
      speedMBps: 0,
      etaSeconds: null,
      error: null,
    });

    try {
      const uploadService = uploadServiceRef.current;
      const draftPayload = (() => {
        const tags = (selectedTagListInParent || []).map(t => t._id);
        if (!isMatchVideoSelected) {
          return { type: 'game', tags };
        }
        return {
          type: 'game',
          tags,
          matchMeta: {
            videoFile: selectedFile.name,
            playerName: metaData.playerName,
            opponentName: metaData.opponentName,
            playerPosition: metaData.playerSide === 'near' ? 'near_side' : 'far_side',
            matchDate: metaData.matchDate,
            location: metaData.location,
            event: metaData.event,
            matchType: metaData.matchType
          }
        };
      })();

      const result = await uploadService.uploadFile(
        selectedFile,
        folderPath,
        (progress) => {
          setUploadState((prev) => {
            const loadedDiff = progress.loaded - prev.loaded;
            const elapsed = (Date.now() - uploadStartTimeRef.current) / 1000;
            const speedMBps = elapsed > 0 ? (progress.loaded / (1024 * 1024)) / elapsed : 0;

            speedSamplesRef.current = [
              ...speedSamplesRef.current.slice(-5),
              speedMBps,
            ];
            const avgSpeed =
              speedSamplesRef.current.reduce((a, b) => a + b, 0) /
              speedSamplesRef.current.length;

            const remainingBytes = progress.total - progress.loaded;
            const etaSeconds =
              avgSpeed > 0
                ? Math.ceil(remainingBytes / (1024 * 1024) / avgSpeed)
                : null;

            return {
              status: "uploading",
              progress: progress.percentage,
              loaded: progress.loaded,
              total: progress.total,
              speedMBps: avgSpeed,
              etaSeconds,
              error: null,
            };
          });
        },
        draftPayload
      );

      setUploadState({
        status: "completed",
        progress: 100,
        loaded: selectedFile.size,
        total: selectedFile.size,
        speedMBps: 0,
        etaSeconds: 0,
        error: null,
      });

      if (typeof window !== "undefined") {
        TagHelper.persistTags(result.postId, result.key, selectedTagListInParent || []);
      }

      dispatch(fetchSubscriptionUsage(jwtToken, "smartInsights"));

      setUploadedVideoData({
        key: result.key,
        location: result.location,
        bucket: result.bucket,
        etag: result.etag,
        s3Config: result.s3Config,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        tags: selectedTagListInParent,
        meta: metaData,
        postId: result.postId,
      });

      if (result && result.postId) {
        localStorage.setItem('newlyUploadedVideoId', result.postId);
      }

      setShowCompletionDialog(true);
      setIsUploading(false);
    } catch (error) {
      console.error("Upload error:", error);
      let errorMessage = "Upload failed";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      const isDuplicate = error.response?.status === 409 && error.response?.data?.details?.duplicateFilename;

      setUploadState((prev) => ({
        ...prev,
        status: "failed",
        error: errorMessage,
      }));

      if (isDuplicate) {
        setDuplicateFileError(errorMessage);
      } else {
        dispatch(setAlertType("error"));
        dispatch(setAlertMessage(errorMessage));
      }

      setIsUploading(false);
    }
  }, [selectedFile, jwtToken, dispatch, folderPath, metaData, selectedTagListInParent, isMatchVideoSelected]);

  const handleUploadClick = useCallback(() => {
    if (!selectedFile) {
      dispatch(setAlertType("error"));
      dispatch(setAlertMessage("Please select a video to upload"));
      return;
    }
    if (isMatchVideoSelected) {
      setShowMetaForm(true);
    } else {
      handleStartUpload();
    }
  }, [isMatchVideoSelected, handleStartUpload, selectedFile, dispatch]);

  const handleMetaChange = useCallback((e) => {
    const { name, value } = e.target;
    setMetaData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "playerSide") {
        next.playerDesignation = value === "near" ? "B" : "A";
      }
      return next;
    });
  }, []);

  const handleMetaSubmit = useCallback(() => {
    if (!metaData.playerName || !metaData.opponentName || !metaData.matchDate || !metaData.location || !metaData.event || !metaData.matchType) {
      setMetaError("Please fill all fields");
      return;
    }
    setMetaError("");
    setShowMetaForm(false);
    handleStartUpload();
  }, [metaData, handleStartUpload]);

  const handleCloseMeta = useCallback(() => {
    setShowMetaForm(false);
  }, []);

  const handlePause = useCallback(() => {
    const uploadService = uploadServiceRef.current;
    if (uploadService) {
      uploadService.pause();
      setIsPaused(true);
      setUploadState((prev) => ({
        ...prev,
        status: "paused",
      }));
      try {
        ReactGA.event({ category: "Upload", action: "pause" });
      } catch { }
    }
  }, []);

  const handleResume = useCallback(() => {
    if (!isPaused) return;
    const uploadService = uploadServiceRef.current;
    if (uploadService) {
      uploadService.resume();
      setIsPaused(false);
      setUploadState((prev) => ({
        ...prev,
        status: "uploading",
      }));
      try {
        ReactGA.event({ category: "Upload", action: "resume" });
      } catch { }
    }
  }, [isPaused]);

  const handleCancel = useCallback(() => {
    const uploadService = uploadServiceRef.current;
    if (uploadService) {
      uploadService.cancel();
    }
    setSelectedFile(null);
    setUploadState(initialUploadState);
    setIsUploading(false);
    setIsPaused(false);
    try {
      ReactGA.event({ category: "Upload", action: "cancel" });
    } catch { }
  }, []);

  const redirectToLibrary = useCallback(() => {
    setShowCompletionDialog(false);
    setSelectedFile(null);
    setUploadState(initialUploadState);
    setIsUploading(false);
    setIsPaused(false);
    const nextState = uploadedVideoData ? { newVideoData: uploadedVideoData } : undefined;
    navigate("/game-posts/game-videos", { replace: true, state: nextState });
    if (onUploadComplete) {
      onUploadComplete();
    }
  }, [navigate, uploadedVideoData, onUploadComplete]);

  const handleCloseDialog = useCallback(() => {
    redirectToLibrary();
    try {
      ReactGA.event({ category: "Upload", action: "completion_confirmed" });
    } catch { }
  }, [redirectToLibrary]);

  useEffect(() => {
    if (showCompletionDialog) {
      const timer = setTimeout(() => {
        localStorage.setItem('uploadSuccess', 'true');
        if (onUploadComplete) {
          onUploadComplete();
        } else {
          handleCloseDialog();
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showCompletionDialog, onUploadComplete, handleCloseDialog]);

  const showProgress = uploadState.status !== "idle" && !!selectedFile;

  return (
    <>
      <Dialog
        open={showMetaForm}
        onClose={handleCloseMeta}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #0f1621 0%, #0a121b 100%)',
            boxShadow: '0 20px 60px rgba(16, 185, 129, 0.2)',
            border: '1px solid rgba(94, 208, 137, 0.2)'
          },
        }}
      >
        <DialogContent sx={{ padding: '28px 24px' }}>
          <h2 className={styles.metaTitle}>Match Video Details</h2>
          <div className={styles.metaForm}>
            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label>Player Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input name="playerName" value={metaData.playerName} onChange={handleMetaChange} placeholder="Enter player name" />
              </div>
              <div className={styles.field}>
                <label>Opponent Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input name="opponentName" value={metaData.opponentName} onChange={handleMetaChange} placeholder="Enter opponent name" />
              </div>
              <div className={styles.field}>
                <label>Player Position Relative to Camera</label>
                <div className={styles.radioGroup}>
                  <label><input type="radio" name="playerSide" value="near" checked={metaData.playerSide === 'near'} onChange={handleMetaChange} /> Near Side</label>
                  <label><input type="radio" name="playerSide" value="far" checked={metaData.playerSide === 'far'} onChange={handleMetaChange} /> Far Side</label>
                </div>
              </div>
              <div className={styles.field}>
                <label>Match Date</label>
                <input type="date" name="matchDate" value={metaData.matchDate} onChange={handleMetaChange} />
              </div>
              <div className={styles.field}>
                <label>Location</label>
                <input name="location" value={metaData.location} onChange={handleMetaChange} placeholder="Enter match location" />
              </div>
              <div className={styles.field}>
                <label>Event</label>
                <input name="event" value={metaData.event} onChange={handleMetaChange} placeholder="Enter event/tournament name" />
              </div>
              <div className={styles.field}>
                <label>Match Type</label>
                <div className={styles.radioGroup}>
                  <label><input type="radio" name="matchType" value="singles" checked={metaData.matchType === 'singles'} onChange={handleMetaChange} /> Singles</label>
                </div>
              </div>
            </div>
            {metaError ? <p className={styles.errorText}>{metaError}</p> : null}
            <div className={styles.dialogActions}>
              <button type="button" onClick={handleCloseMeta}>Cancel</button>
              <button type="button" onClick={handleMetaSubmit}>Start Upload</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showCompletionDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            boxShadow: '0 20px 60px rgba(16, 185, 129, 0.4)',
          },
        }}
      >
        <DialogContent sx={{ padding: '60px 40px', textAlign: 'center' }}>
          <div style={{ animation: 'fadeInScale 0.6s ease-out' }}>
            <div
              style={{
                fontSize: '72px',
                marginBottom: '24px',
                animation: 'bounce 1s ease-in-out infinite',
              }}
            >
              ✅
            </div>
            <h2 style={{ color: '#fff', fontSize: '28px', fontWeight: 700, marginBottom: '16px' }}>
              Upload Successful!
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px', marginBottom: '8px' }}>
              {uploadedVideoData?.fileName}
            </p>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', marginBottom: '32px' }}>
              {((uploadedVideoData?.fileSize || 0) / (1024 * 1024)).toFixed(2)} MB • Redirecting...
            </p>
          </div>
          <style>{`
            @keyframes fadeInScale {
              from {
                opacity: 0;
                transform: scale(0.8);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes bounce {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-20px);
              }
            }
          `}</style>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!duplicateFileError}
        onClose={() => setDuplicateFileError(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #0f1621 0%, #0a121b 100%)',
            boxShadow: '0 20px 60px rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          },
        }}
      >
        <DialogContent sx={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ animation: 'fadeInScale 0.4s ease-out' }}>
            <div
              style={{
                fontSize: '64px',
                marginBottom: '16px',
              }}
            >
              ⚠️
            </div>
            <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 600, marginBottom: '16px' }}>
              Duplicate File
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px', marginBottom: '32px', lineHeight: '1.5' }}>
              {duplicateFileError}
            </p>
            <button
              type="button"
              onClick={() => setDuplicateFileError(null)}
              style={{
                background: '#ef4444',
                border: 'none',
                color: '#fff',
                padding: '12px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
              onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
            >
              Okay
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <div className={styles.uploadWrapper}>
        <div className={styles.headerRow}>
          <div>
            <h1>Upload your video</h1>
            <p className={styles.subheading}>Drag, drop, pause, resume — your upload, fully in control.</p>
          </div>
          <button type="button" className={styles.libraryButton} onClick={redirectToLibrary}>Go to Library</button>
        </div>
        <div className={styles.body}>
          <div
            className={styles.dropzone}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            role="presentation"
          >
            <div className={styles.dropzoneContent}>
              <div
                className={styles.browsePrompt}
                role="button"
                tabIndex={0}
                onClick={handleBrowseContainerClick}
                onKeyDown={handleBrowseContainerKeyDown}
              >
                <h2>Drag & Drop</h2>
                <p className={styles.supported}>Files supported: MP4 only</p>
                <p className={styles.supported}>Size limit: 2GB</p>
                <p className={styles.browseCopy}>
                  or <span className={styles.browseLink}>browse files</span> on your computer
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInput}
                accept=".mp4,video/mp4"
                aria-label="Choose file to upload"
              />
              {selectedFile && (
                <div className={styles.fileMeta}>
                  <span className={styles.fileName}>{selectedFile.name}</span>
                  {formattedSize && <span className={styles.fileSize}>{formattedSize}</span>}
                </div>
              )}

            </div>
            {showProgress && (
              <div className={styles.progressPanel}>
                <div className={styles.progressHeader}>
                  <div>
                    <p className={styles.statusLabel}>{statusLabel}</p>
                    <p className={styles.waitMessage}>{waitMessage}</p>
                  </div>
                  <div className={styles.progressMetrics}>
                    <span>{speedLabel}</span>
                    <span>{estimatedTime}</span>
                  </div>
                </div>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${uploadState.progress}%` }} />
                </div>
                <div className={styles.progressStats}>
                  <span>{uploadState.progress}%</span>
                  <span>{uploadState.loaded ? `${(uploadState.loaded / (1024 * 1024)).toFixed(2)} MB` : "0 MB"}</span>
                </div>
                <div className={styles.actionsRow}>
                  <button type="button" onClick={isPaused ? handleResume : handlePause}>
                    {isPaused ? "Resume" : "Pause"}
                  </button>
                  <button type="button" onClick={handleCancel}>Cancel</button>
                </div>
              </div>
            )}
            <VideoTagsSelectContainer
              selectedTagList={selectedTagListInParent}
              setSelectedTagList={setSelectedTagListInParent}
            />
            <div className={styles.primaryActions}>
              <button type="button" onClick={handleUploadClick} disabled={isUploading}>
                {isUploading ? "Uploading" : "Upload"}
              </button>
            </div>
          </div>
          <div className={styles.guidelinesPanel}>
            <div className={styles.videoPreview}>
              <video width="100%" controls>
                <source src="assets/GuidelinesVideo.mp4" type="video/mp4" />
              </video>
            </div>
            <div className={styles.guidelinesBox}>
              <h2>Guidelines to Record and Upload</h2>
              <div className={styles.guidelinesList}>
                {guidelines.map((item) => (
                  <div key={item.id} className={styles.guidelineItem}>
                    <Avatar
                      sx={{
                        width: {
                          xs: "32px",
                          md: "40px",
                          lg: "48px",
                        },
                        height: {
                          xs: "32px",
                          md: "40px",
                          lg: "48px",
                        },
                        backgroundColor: "#247E45",
                        fontWeight: 600,
                      }}
                    >
                      {item.id === "1" && <Camera size={24} color="white" />}
                      {item.id === "2" && <Sun size={24} color="white" />}
                      {item.id === "3" && <FileVideo size={24} color="white" />}
                      {item.id === "4" && <Globe size={24} color="white" />}
                    </Avatar>
                    <p>{item.guideline}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
